import type { JsComponent, NoodlNode } from '@shared/node-v1.0.0';
import { sendSignal } from '@shared/port-send-v1.0.0';
import type { Props } from '../node/definition';
import { fetchLocalData } from './fetchLocalData';

export const unsubscribe = (p: Props) => {
	// Если нет подписок
	if (!p.store.subscribes.length) return;

	// Отписываемся
	for (const iSubscribe of p.store.subscribes) {
		try {
			if (iSubscribe && typeof iSubscribe.unsubscribe === 'function') {
				iSubscribe.unsubscribe();
			}
		} catch (err) {
			console.error('Ошибка при отписке:', err);
		}
	}

	// И очищаем массив подписок
	p.store.subscribes = [];
};

const reactive = async (p: Props, noodlNode: NoodlNode) => {
	const { compare } = R.libs.just;

	// Загрузим данные при включении реактивного режима в редакторе.
	if (p.store.controlled !== p.controlled) {
		if (p.store.controlled && !p.controlled) await fetchLocalData(p, noodlNode);
		p.store.controlled = p.controlled;
	}
	// Сравним схему.
	if (!compare(p.store.fetchScheme, p.fetchScheme)) {
		p.store.fetchScheme = p.fetchScheme;
		// Если схемы отличаются, то отписываемся от старых
		unsubscribe(p);
		if (!p.controlled) await fetchLocalData(p, noodlNode);
	}
	// Отреагируем на включение/отключение подписки.
	if (p.store.subscribe !== p.subscribe) {
		p.store.subscribe = p.subscribe;
		await fetchLocalData(p, noodlNode); // Нужно перезагрузить данные, т.к. подписки прилетают с сервреа.
		if (!p.subscribe) unsubscribe(p);
	}

	// Если включена пагинация, то нам нужна схема пагинируемого класса
	const paginationScheme = p.paginationOn ? p.fetchScheme?.find((fScheme) => fScheme.dbClass === p.paginationClass) : undefined;

	// Если изменился p.paginationOn
	if (p.store.paginationOn !== p.paginationOn) {
		// Если он был выключен, и его включили
		if (p.paginationOn) {
			// Устанавливаем страницу в 1
			p.store.page = 1;
			// Устанавливаем from в 0
			p.store.from = 0;
			// Получаем size у пагинируемого класса
			p.store.size = paginationScheme?.size || 0;
		}
	}

	// Если изменился paginationClass
	if (p.store.paginationClass !== p.paginationClass) {
		// При смене класса устанавливаем номер страницы в начальное значение
		p.store.page = 1;
		// Устанавливаем from в 0
		p.store.from = 0;
		// Сохраняем в store название класса
		p.store.paginationClass = p.paginationClass;
		// Нужно перезагрузить данные, т.к. сменился класс для пагинации
		// Не проверяем на if (!p.controlled), так как смена "paginationClass" должн и так привести к получению новых данных
		await fetchLocalData(p, noodlNode);
	}

	// При смене size пагинируемого класса, устанавливаем номер страницы в 1
	if (p.store.size !== paginationScheme?.size && p.store.page !== 1) {
		// При смене size номер страницы в начальное значение, если ему и не равно
		p.store.page = 1;
		// Устанавливаем from в 0
		p.store.from = 0;
		// Нужно перезагрузить данные, т.к. изменился size
		// Не проверяем на if (!p.controlled), так как смена "size" должна и так привести к получению новых данных
		await fetchLocalData(p, noodlNode);
	}

	// Первый проход.
	if (!p.store.inited) {
		// Первичная загрузка реактивного режима.
		if (!p.controlled) await fetchLocalData(p, noodlNode);

		// Тригеры. Для реактивного режима, т.к. в контроллируемом еще нет rootNode. Для контроллируемого в сигнале fetch.
		const rootNode = R.nodes[p.store.rootId];
		if (rootNode) {
			// Смена состояний.
			Noodl.Events.on(`${rootNode.path}_singleSelectionChanged`, () => sendSignal(noodlNode, 'singleSelectionChanged'));
			Noodl.Events.on(`${rootNode.path}_multiSelectionChanged`, () => sendSignal(noodlNode, 'multiSelectionChanged'));
			Noodl.Events.on(`${rootNode.path}_expansionChanged`, () => sendSignal(noodlNode, 'expansionChanged'));
		}

		p.store.inited = true;
	}

	return;
};

export default {
	reactive, // Главная функция, объявлена выше
	fetchLocalData: async (p: Props, noodlNode) => {
		await fetchLocalData(p, noodlNode);

		// Первый проход.
		if (!p.store.signalsInited) {
			// Тригеры. Для контроллируемого режима. Для реактивного в reactive.
			const rootNode = R.nodes[p.store.rootId];
			if (rootNode) {
				// Смена состояний.
				Noodl.Events.on(`${rootNode.path}_singleSelectionChanged`, () => sendSignal(noodlNode, 'singleSelectionChanged'));
				Noodl.Events.on(`${rootNode.path}_multiSelectionChanged`, () => sendSignal(noodlNode, 'multiSelectionChanged'));
				Noodl.Events.on(`${rootNode.path}_expansionChanged`, () => sendSignal(noodlNode, 'expansionChanged'));
			}

			p.store.signalsInited = true;
		}
	},
	resetSingleSelection: (p: Props, noodlNode) => {
		for (const i of Object.values(R.nodes).filter((i) => i.rootId === p.store.rootId)) i.states.singleSelection.value = null;

		sendSignal(noodlNode, 'singleSelectionChanged');
		if (!p.controlled) reactive(p, noodlNode);
	},
	resetMultiSelection: (p: Props, noodlNode) => {
		for (const i of Object.values(R.nodes).filter((i) => i.rootId === p.store.rootId))
			i.states.multiSelection.value = 'notSelected';

		sendSignal(noodlNode, 'multiSelectionChanged');
		if (!p.controlled) reactive(p, noodlNode);
	},
	expandAll: (p: Props, noodlNode) => {
		for (const i of Object.values(R.nodes).filter((i) => i.rootId === p.store.rootId)) i.states.expansion.value = true;

		sendSignal(noodlNode, 'expansionChanged');
		if (!p.controlled) reactive(p, noodlNode);
	},
	collapseAll: (p: Props, noodlNode) => {
		for (const i of Object.values(R.nodes).filter((i) => i.rootId === p.store.rootId)) i.states.expansion.value = false;

		sendSignal(noodlNode, 'expansionChanged');
		if (!p.controlled) reactive(p, noodlNode);
	},
	previousFetch: async (p: Props, noodlNode) => {
		// Должны понизить номер страницы с текущего и вернуть данные для получившейся страницы
		if (p.store.page && p.store.totalPagesCount) {
			const currentPage = p.store.page - 1 >= 1 ? p.store.page - 1 : 1;
			if (p.store.page !== currentPage) {
				p.store.page = currentPage;
				await fetchLocalData(p, noodlNode);
			}
		}
	},
	nextFetch: async (p: Props, noodlNode) => {
		// Должны повысить номер страницы с текущего и запустить fetch
		if (p.store.page && p.store.totalPagesCount) {
			const currentPage = p.store.page + 1 <= p.store.totalPagesCount ? p.store.page + 1 : p.store.totalPagesCount;
			if (p.store.page !== currentPage) {
				p.store.page = currentPage;
				await fetchLocalData(p, noodlNode);
			}
		}
	},
	goToPage: async (p: Props, noodlNode) => {
		// Должны повысить номер страницы с текущего и запустить fetch
		if (p.store.totalPagesCount) {
			if (p.toPageNumber && p.store.page !== p.toPageNumber) {
				p.store.page = p.toPageNumber;
				await fetchLocalData(p, noodlNode);
			}
		}
	},
} as JsComponent;
